import { CompactUser } from 'src/context/user';
import { MISTAKE_UPSELL_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';
import { getMilestoneUpsellLastShownAt } from 'src/apps/Monetisation/MilestoneUpsell/milestoneUpsellStorage';

type MistakeUpsellStorage = {
  lastShownAt: string | null;
};

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const emptyStorage = (): MistakeUpsellStorage => ({ lastShownAt: null });

const readStorage = (userId: number): MistakeUpsellStorage => {
  const raw = getLocalStorageEntry(`${MISTAKE_UPSELL_STORAGE_KEY}-${userId}`);
  if (!raw) return emptyStorage();

  try {
    const parsed = JSON.parse(raw) as MistakeUpsellStorage;
    return { lastShownAt: parsed.lastShownAt ?? null };
  } catch {
    return emptyStorage();
  }
};

const writeStorage = (userId: number, value: MistakeUpsellStorage) => {
  setLocalStorageEntry(`${MISTAKE_UPSELL_STORAGE_KEY}-${userId}`, JSON.stringify(value));
};

const isSameCalendarDay = (timeA: number, timeB: number): boolean =>
  new Date(timeA).toDateString() === new Date(timeB).toDateString();

export const canShowMistakeUpsell = (user: CompactUser | null, now = Date.now()): boolean => {
  if (!user || user.isPro) return false;

  const milestoneLastShownAt = getMilestoneUpsellLastShownAt(user.id);
  if (milestoneLastShownAt) {
    const milestoneShown = Date.parse(milestoneLastShownAt);
    if (!Number.isNaN(milestoneShown) && isSameCalendarDay(milestoneShown, now)) return false;
  }

  const storage = readStorage(user.id);
  if (storage.lastShownAt) {
    const lastShown = Date.parse(storage.lastShownAt);
    if (!Number.isNaN(lastShown) && now - lastShown < SEVEN_DAYS_MS) return false;
  }

  return true;
};

export const markMistakeUpsellShown = (user: CompactUser | null, now = Date.now()) => {
  if (!user) return;

  writeStorage(user.id, { lastShownAt: new Date(now).toISOString() });
};
